import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";

const SHOPIFY_PRODUCT_URL = "https://gpdu9v-ph.myshopify.com/products/moke-ha-h-stack-ca-h-tee.json";
const SHOPIFY_FALLBACK_VARIANT = "58787786129740";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MASCOT = "https://customer-assets-jt897jd0.emergentagent.net/job_smoke-beach-drop/artifacts/g2ejgl6q_maskotka-obrys.webp";
const TEE_FRONT = "https://customer-assets-jt897jd0.emergentagent.net/job_smoke-beach-drop/artifacts/5vqndgmu_front-tee.png";
const TEE_BACK = "https://customer-assets-jt897jd0.emergentagent.net/job_smoke-beach-drop/artifacts/2o0hb98b_back-tee.png";

const SOLD_OUT = [
  { name: "WE AIN'T SMOKING HOODIE", price: "420,00 PLN", img: "https://customer-assets.emergentagent.com/job_freedom-drop/artifacts/6wvpfth1_hoodie-front.png" },
  { name: "WE AIN'T SMOKING SHORTS", price: "280,00 PLN", img: "https://customer-assets.emergentagent.com/job_freedom-drop/artifacts/15ql3ycf_nowe%20zdj%20spodenek.png" },
  { name: "DOUBLE CUP $IGN UP!", price: "48,88 PLN", img: "https://customer-assets.emergentagent.com/job_freedom-drop/artifacts/9aj5y0qx_doublecup.gif" },
];

const SIZES = ["S/M", "L/XL"];

export const Products = ({ t, lang, modalOpen, setModalOpen }) => {
  const [size, setSize] = useState("S/M");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [hoverTee, setHoverTee] = useState(false);
  const [variants, setVariants] = useState({});
  const [stock, setStock] = useState({ sold: 89, left: 91, total: 180, live: false });

  useEffect(() => {
    const load = () =>
      axios.get(`${API}/stock`).then((r) => setStock(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetch(SHOPIFY_PRODUCT_URL)
      .then((r) => r.json())
      .then((d) => {
        const map = {};
        for (const v of d.product.variants) {
          if (["S/M", "L/XL"].includes(v.title)) map[v.title] = v.id;
        }
        setVariants(map);
      })
      .catch(() => {});
  }, []);

  const variantId = variants[size] || SHOPIFY_FALLBACK_VARIANT;
  const buyHref = variants[size]
    ? `https://gpdu9v-ph.myshopify.com/cart/${variantId}:1`
    : `https://gpdu9v-ph.myshopify.com/cart/${variantId}:1?attributes[Rozmiar]=${encodeURIComponent(size)}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error(t.modal.invalid);
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/drop-signup`, { email: email.trim(), size, lang });
      toast.success(data.duplicate ? t.modal.duplicate : t.modal.success);
      if (!data.duplicate) setEmail("");
      setModalOpen(false);
    } catch {
      toast.error(t.modal.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="drop" data-testid="products-section" className="halftone-light bg-[#0a0a0a] py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.h2
          className="toon-text font-script text-4xl text-[#f4f1ea] sm:text-6xl"
          initial={{ opacity: 0, y: 30, rotate: -2 }}
          whileInView={{ opacity: 1, y: 0, rotate: -1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
        >
          {t.products.title}
        </motion.h2>

        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          <motion.div
            data-testid="tee-card"
            className="toon-card group relative -rotate-[0.6deg] bg-[#f4f1ea]"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75, ease: [0.34, 1.56, 0.64, 1] }}
            onMouseEnter={() => setHoverTee(true)}
            onMouseLeave={() => setHoverTee(false)}
          >
            <div className="halftone relative aspect-[4/3] overflow-hidden rounded-t-[15px] bg-[#e8dcc3]">
              <img
                src={hoverTee ? TEE_BACK : TEE_FRONT}
                alt="$MOKE HA$H STACK CA$H tee"
                className="h-full w-full object-cover mix-blend-multiply transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <span data-testid="tee-available-badge" className="toon-chip absolute left-4 top-4 flex rotate-[-3deg] items-center gap-2 bg-[#d96b43] px-3.5 py-1.5 font-script text-sm text-[#0a0a0a]">
                {t.products.tee.available}
              </span>
              <span className="toon-chip absolute right-4 top-4 rotate-[3deg] bg-[#0a0a0a] px-3 py-1.5 font-mono2 text-[10px] font-bold tracking-[0.2em] text-[#e5b83b]">
                {hoverTee ? "BACK" : "FRONT"}
              </span>
            </div>
            <div className="p-6 text-[#0a0a0a] sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-script text-3xl">{t.products.tee.name}</h3>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-[#0a0a0a]/70">{t.products.tee.desc}</p>
                </div>
                <span data-testid="tee-price" className="font-script text-3xl text-[#d96b43]" style={{ textShadow: "2px 2px 0 #0a0a0a" }}>{t.products.tee.price}</span>
              </div>
              <div className="mt-5" data-testid="size-chart">
                <span className="font-mono2 text-[10px] font-bold tracking-[0.3em] text-[#0a0a0a]/60">{t.products.tee.sizechart}</span>
                <table className="mt-2 w-full max-w-xs border-[3px] border-[#0a0a0a] font-mono2 text-xs text-[#0a0a0a] shadow-[4px_4px_0_#0a0a0a]">
                  <tbody>
                    <tr className="border-b-[3px] border-[#0a0a0a] bg-[#d96b43]">
                      <td className="px-3 py-2 font-bold">S/M</td>
                      <td className="px-3 py-2 text-right font-bold">60 x 70 cm</td>
                    </tr>
                    <tr className="border-b-[3px] border-[#0a0a0a] bg-white">
                      <td className="px-3 py-2 font-bold">L/XL</td>
                      <td className="px-3 py-2 text-right font-bold">65 x 75 cm</td>
                    </tr>
                    <tr className="bg-[#f4f1ea]">
                      <td colSpan="2" className="px-3 py-1.5 text-[10px] tracking-[0.15em] text-[#0a0a0a]/50">{t.products.tee.accuracy}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-6" data-testid="stock-counter">
                <div className="flex items-end justify-between">
                  <span className="font-mono2 text-[10px] font-bold tracking-[0.3em] text-[#0a0a0a]/60">
                    {t.products.tee.sold} {stock.sold} {t.products.tee.stockof}
                    {stock.live && <span data-testid="stock-live-dot" className="ml-2 inline-block h-2 w-2 animate-pulse rounded-full bg-[#2e6b3e] align-middle" />}
                  </span>
                  <span className="font-script text-2xl leading-none text-[#d96b43]" style={{ textShadow: "2px 2px 0 #0a0a0a" }}>
                    {t.products.tee.left} {stock.left} {t.products.tee.pcs}
                  </span>
                </div>
                <div className="mt-2 h-5 w-full overflow-hidden rounded-full border-[3px] border-[#0a0a0a] bg-white shadow-[3px_3px_0_#0a0a0a]">
                  <motion.div
                    className="h-full bg-[#d96b43]"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${(stock.sold / stock.total) * 100}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
              </div>
              <div className="mt-6">
                <span className="font-mono2 text-[10px] font-bold tracking-[0.3em] text-[#0a0a0a]/60">{t.products.tee.size}</span>
                <div className="mt-2 flex flex-wrap gap-2.5" data-testid="size-selector">
                  {SIZES.map((s) => (
                    <button
                      key={s}
                      data-testid={`size-${s}`}
                      onClick={() => setSize(s)}
                      className={`btn-toon h-11 w-12 text-lg ${size === s ? "bg-[#d96b43] text-[#0a0a0a]" : "bg-[#f4f1ea] text-[#0a0a0a]/60"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <a
                data-testid="tee-buy-button"
                href={buyHref}
                target="_blank"
                rel="noreferrer"
                className="btn-toon mt-8 block w-full bg-[#0a0a0a] py-4 text-center text-xl text-[#e5b83b]"
              >
                {t.products.tee.cta}
              </a>
            </div>
          </motion.div>

          <div className="grid gap-8 sm:grid-cols-2">
            {SOLD_OUT.map((p, i) => (
              <motion.div
                key={p.name}
                data-testid={`soldout-card-${i}`}
                className={`soldout-card toon-card relative overflow-hidden bg-[#1a1a1e] ${i === 2 ? "sm:col-span-2" : ""} ${i % 2 ? "rotate-[1deg]" : "-rotate-[1deg]"}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + i * 0.1, duration: 0.65, ease: [0.34, 1.56, 0.64, 1] }}
              >
                <div className="relative aspect-square overflow-hidden">
                  <img src={p.img} alt={p.name} className="soldout-img h-full w-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span data-testid={`soldout-badge-${i}`} className="toon-card rotate-[-9deg] bg-[#d96b43] px-6 py-2.5 font-script text-3xl text-[#0a0a0a]">
                      {t.products.soldout}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between border-t-[3px] border-[#0a0a0a] bg-[#f4f1ea] p-4">
                  <span className="font-mono2 text-[11px] font-bold tracking-[0.12em] text-[#0a0a0a]">{p.name}</span>
                  <span className="font-mono2 text-[11px] font-bold text-[#0a0a0a]/50 line-through">{p.price}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {modalOpen && (
        <div
          data-testid="signup-modal"
          className="fixed inset-0 z-[90] flex items-center justify-center bg-[#0a0a0a]/85 p-4 backdrop-blur-md"
          onClick={() => setModalOpen(false)}
        >
          <motion.div
            className="toon-card relative w-full max-w-md rotate-[-1deg] bg-[#f4f1ea] p-8"
            initial={{ opacity: 0, scale: 0.85, y: 30, rotate: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0, rotate: -1 }}
            transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="modal-close-button" onClick={() => setModalOpen(false)} className="absolute right-4 top-4 text-[#0a0a0a]/60 transition-colors hover:text-[#d96b43]">
              <X size={22} />
            </button>
            <div className="flex items-center gap-4">
              <img src={MASCOT} alt="" className="h-16 w-16 object-contain" />
              <h3 className="font-script text-3xl text-[#0a0a0a]">{t.modal.title}</h3>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-[#0a0a0a]/70">{t.modal.sub}</p>
            <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
              <input
                data-testid="modal-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.modal.placeholder}
                className="w-full rounded-xl border-[3px] border-[#0a0a0a] bg-white px-4 py-3.5 font-mono2 text-sm text-[#0a0a0a] shadow-[4px_4px_0_#0a0a0a] outline-none placeholder:text-[#0a0a0a]/40 focus:shadow-[2px_2px_0_#0a0a0a]"
              />
              <button
                data-testid="modal-submit-button"
                type="submit"
                disabled={sending}
                className="btn-toon bg-[#d96b43] py-3.5 text-xl text-[#0a0a0a] disabled:opacity-50"
              >
                {sending ? "…" : t.modal.submit}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </section>
  );
};
